// of-progress-banner.js
// Handles progress banner behavior (moved out of Liquid).
// Reads data- attributes from [data-fsb-root] and #cart-metadata.
//
// 2026-08-29 (cart drawer P1-6). The boot and element-binding half of this
// file was rewritten; the math and the flag logic between them were not
// touched. What changed and why:
//
//   1. The banner element is resolved on EVERY render instead of once at
//      IIFE time. The old code did `const banner = getElementById(...)`
//      followed by `if (!banner) return`, which ran before any page had
//      finished loading and killed the script outright on every page that
//      is not /cart. There was no re-entry path at all. The drawer renders
//      this banner on every other page type, and re-renders it on a
//      section swap, so a reference captured once is a reference that
//      goes stale.
//   2. Element lookups are scoped to that banner rather than to document.
//      The selectors are unchanged; only their root moved.
//   3. window.__OFProgressBanner.refresh() is the documented re-entry
//      point, and of:cart:updated carries the new root.
//   4. GA4 events carry a `surface` of cart or drawer, and view_cart only
//      fires where a human is actually looking at a cart: on /cart, or in
//      the drawer once it is open. Without that gate the drawer would fire
//      view_cart on every pageview of the site and the 30-day baseline
//      would be worthless.

(function () {
  if (window.ofProgressBannerInit) return; // prevent double-init
  window.ofProgressBannerInit = true;

  // CONFIG
  const CART_JSON = '/cart.js';

  // GA4: state tracking for free shipping transitions
  var _freeshipState = { valueOk: null, weightOk: null, prevWeight: 0, prevItems: [] };

  // The banner this render is bound to. Assigned at the top of every
  // updateProgress() call, which is what makes $() below root-scoped.
  var banner = null;

  // Helpers. $ defaults to the CURRENT banner, not to document, so the
  // same selectors work no matter where the banner is rendered.
  function $(sel, root) { return (root || banner || document).querySelector(sel); }
  function $all(sel, root) { return Array.from((root || banner || document).querySelectorAll(sel)); }

  // The banner lives inside static-cart on /cart and inside the cart
  // drawer everywhere else. Never both: the drawer is not rendered on the
  // cart page.
  function resolveBanner() {
    return document.querySelector('[data-fsb-root]') || document.getElementById('free-shipping-banner');
  }
  function drawerOf(el) {
    return el && el.closest ? el.closest('of-cart-drawer') : null;
  }
  function surfaceOf(el) {
    return drawerOf(el) ? 'drawer' : 'cart';
  }

  // The one hook the Free Shipping Top-Off row (assets/of-cart-topoff.js)
  // listens on. Fired at every point this file SETTLES a render, including
  // the special and hidden paths, so the top-off can hide itself the same
  // instant the banner does rather than reading a stale gap. Nothing here
  // recomputes anything the render above did not already decide; this only
  // publishes it. `special` distinguishes WHY the row cannot show from a
  // healthy `null`, since a cart already over the weight cap or already
  // carrying a hazardous/oversized item must never receive a top-off
  // suggestion regardless of how small the dollar gap reads.
  function publishFreeshipState(state) {
    try {
      document.dispatchEvent(new CustomEvent('of:freeship:state', {
        detail: Object.assign({ surface: surfaceOf(banner) }, state)
      }));
    } catch (e) {}
  }
  // True when the customer can actually see this banner right now.
  // The shipping estimator (snippets/cart-shipping.liquid, /cart only) has
  // never been measured: Empire ships no analytics for it and no of_ event
  // existed, so the "does it earn a drawer slot" question had no data
  // (Rhett 8/29). Two events now: opening the calculator, and actually
  // asking for a rate. The 30-day read decides whether the drawer needs it.
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest || !window.OFTrack) return;
    if (t.closest('[data-cartshipping-submit]')) {
      OFTrack.event('of_ship_estimate', { surface: 'cart' });
    } else if (t.closest('[data-cartshipping-toggle]')) {
      OFTrack.once('of_ship_estimate_open', 'of_ship_estimate_open', { surface: 'cart' });
    }
  });

  function isVisible(el) {
    const d = drawerOf(el);
    return !d || d.getAttribute('open') === 'true';
  }
  function formatMoney(cents) { return '$' + (cents / 100).toFixed(2); }
  // Same shape of-nudges-core.js resolves a failed /cart.js read to, so the
  // raw-fetch fallback and the shared fetcher agree on what failure means.
  function cartReadFailed() { return { items: [], total_price: 0, __ofFetchFailed: true }; }
  function show(el) { if (!el) return; el.style.display = ''; el.setAttribute('aria-hidden', 'false'); }
  function hide(el) { if (!el) return; el.style.display = 'none'; el.setAttribute('aria-hidden', 'true'); }
  function setText(el, txt) { if (!el) return; el.textContent = txt; el.setAttribute('aria-hidden', 'false'); }

  // The order minimum gate on /cart. Liquid renders the initial state; this
  // keeps it honest across AJAX cart edits. USD-only store, so the cents
  // math and the dollar formatting are safe (Rhett 8/31).
  //
  // The number is NOT written here. snippets/of-cart-minimum.liquid is its
  // one source and of-cart-metadata.liquid publishes it as data-min-cents;
  // a copy in this file would be a tenth place to forget. With no attribute
  // to read, this leaves the Liquid-rendered state exactly as it is rather
  // than guessing a threshold and possibly opening a checkout Liquid held.
  function minimumCents() {
    const meta = document.getElementById('cart-metadata');
    const v = meta ? Number(meta.dataset.minCents) : NaN;
    return (isFinite(v) && v > 0) ? v : 0;
  }
  function updateMinimumGate(totalCents) {
    if (typeof totalCents !== 'number') return;
    const minimum = minimumCents();
    if (!minimum) return;
    const gates = document.querySelectorAll('[data-of-min-gate]');
    const note = document.getElementById('of-cart-minimum-note');
    if (!gates.length && !note) return;
    const under = totalCents > 0 && totalCents < minimum;
    gates.forEach((btn) => {
      if (under) {
        btn.disabled = true;
        btn.type = 'button';
        btn.removeAttribute('name');
        btn.classList.add('of-cart-minimum__button');
      } else {
        btn.disabled = false;
        btn.type = 'submit';
        btn.setAttribute('name', 'checkout');
        btn.classList.remove('of-cart-minimum__button');
      }
    });
    if (note) {
      note.hidden = !under;
      const amt = note.querySelector('[data-of-min-remaining]');
      if (under && amt) amt.textContent = '$' + ((minimum - totalCents) / 100).toFixed(2);
    }
    // Shop Pay and friends render below the held button; while the cart is
    // under the line they hide rather than contradict it (Checkout Blocks
    // still enforces the minimum for anyone who gets through).
    document.querySelectorAll('.additional-checkout-buttons').forEach((el) => {
      el.style.display = under ? 'none' : '';
    });
  }

  // Read config from the DOM on every render. These used to be consts
  // captured at IIFE time, which is what made the whole script dead on any
  // page whose banner had not been rendered yet.
  var MIN_VALUE = 250;
  var MAX_WEIGHT = 10;
  function readConfig() {
    MIN_VALUE = Number((banner && banner.dataset.minValue) || 250);
    MAX_WEIGHT = Number((banner && banner.dataset.maxWeight) || 10);
  }

  // Defensive UI setter combining class + inline toggles.
  //
  // Fully self-contained and idempotent: every call (hazardous, oversized,
  // or the reset/default) clears EVERY sub-element the OTHER branches
  // might have left visible, including the tier-specific elements
  // (valueMetEl/weightStatusEl/statusTextEl) and classes that renderTier()
  // owns. That symmetry matters for both transition directions:
  //   - hazardous/oversized -> normal: the reset branch below restores
  //     description/bars and hides hazEl/overEl (unchanged from before).
  //   - eligible (possibly mid-tier, e.g. split-status/status) ->
  //     hazardous/oversized: renderTier() never runs on this call (the
  //     caller returns immediately after), so without clearTierClasses()
  //     and hiding valueMetEl/weightStatusEl/statusTextEl here, a stale
  //     tier message from the PREVIOUS render could still show underneath
  //     the hazard/oversize message.
  function setSpecialState({ hazardous = false, oversized = false } = {}) {
    const headerEl = $('#progress-header');
    const descriptionEl = $('.progress-banner__description');
    const valueProgress = $('#value-progress');
    const weightProgress = $('#weight-progress');
    const valueText = $('#value-text');
    const weightText = $('#weight-text');
    const hazEl = $('#hazard-text');
    const overEl = $('#oversize-text');
    const valueMetEl = $('#value-met-text');
    const weightStatusEl = $('#weight-status-text');
    const statusTextEl = $('#status-text');

    // when hazardous: show hazard message only
    if (hazardous) {
      clearTierClasses();
      banner.classList.add('progress-banner--hazardous');
      banner.classList.remove('progress-banner--oversized');
      if (headerEl) headerEl.textContent = '⚠️ Hazardous Item In Cart';
      if (descriptionEl) hide(descriptionEl);
      if (valueProgress) hide(valueProgress);
      if (weightProgress) hide(weightProgress);
      if (valueText) hide(valueText);
      if (weightText) hide(weightText);
      if (valueMetEl) hide(valueMetEl);
      if (weightStatusEl) hide(weightStatusEl);
      if (statusTextEl) hide(statusTextEl);
      if (hazEl) show(hazEl);
      if (overEl) hide(overEl);
      return;
    }

    // when oversized: show oversize message only
    if (oversized) {
      clearTierClasses();
      banner.classList.add('progress-banner--oversized');
      banner.classList.remove('progress-banner--hazardous');
      if (headerEl) headerEl.textContent = '❌ Oversized Item In Cart';
      if (descriptionEl) hide(descriptionEl);
      if (valueProgress) hide(valueProgress);
      if (weightProgress) hide(weightProgress);
      if (valueText) hide(valueText);
      if (weightText) hide(weightText);
      if (valueMetEl) hide(valueMetEl);
      if (weightStatusEl) hide(weightStatusEl);
      if (statusTextEl) hide(statusTextEl);
      if (overEl) show(overEl);
      if (hazEl) hide(hazEl);
      return;
    }

    // normal state: restore. renderTier() always runs immediately after
    // this in updateProgress()'s normal-path flow and owns the tier
    // classes + valueMetEl/weightStatusEl/statusTextEl (it resets those
    // itself at its own top), so this branch doesn't duplicate that.
    // clearTierClasses() here is still defensive: correct even if some
    // future caller invokes setSpecialState() without a following
    // renderTier() call.
    clearTierClasses();
    banner.classList.remove('progress-banner--hazardous', 'progress-banner--oversized');
    if (headerEl) headerEl.textContent = '🚚 Unlock Free Shipping';
    if (descriptionEl) show(descriptionEl);
    if (valueProgress) show(valueProgress);
    if (weightProgress) show(weightProgress);
    if (valueText) show(valueText);
    if (weightText) show(weightText);
    if (hazEl) hide(hazEl);
    if (overEl) hide(overEl);
  }

  // Clear all tier-specific classes from the banner.
  //
  // progress-banner--unlocked is in this list and is not decoration. The
  // drawer suppresses the banner's title row (of-cart-drawer.css), and that
  // took the win sentence with it, because #progress-header is the only
  // element on any surface that ever says "You've unlocked free shipping!".
  // This class is how the stylesheet lets the win through while the
  // redundant "Unlock Free Shipping" title stays hidden.
  function clearTierClasses() {
    banner.classList.remove('progress-banner--split-status', 'progress-banner--status-mode', 'progress-banner--unlocked');
  }

  // Render banner based on weight tier
  function renderTier(tier, totalD, remain, weight) {
    clearTierClasses();
    var valueMet = remain <= 0;
    var valueMetEl = $('#value-met-text');
    var weightStatusEl = $('#weight-status-text');
    var statusTextEl = $('#status-text');
    var descriptionEl = $('.progress-banner__description');
    var vpEl = $('#value-progress');
    var wpEl = $('#weight-progress');
    var vtEl = $('#value-text');
    var wtEl = $('#weight-text');

    // Reset new elements to hidden
    if (valueMetEl) hide(valueMetEl);
    if (weightStatusEl) hide(weightStatusEl);
    if (statusTextEl) hide(statusTextEl);

    if (tier === 'split-status') {
      banner.classList.add('progress-banner--split-status');

      // Hide both bars and their labels
      if (vpEl) hide(vpEl);
      if (wpEl) hide(wpEl);
      if (vtEl) hide(vtEl);
      if (wtEl) hide(wtEl);
      if (valueMetEl) hide(valueMetEl);

      // Single cohesive message based on value status
      if (weightStatusEl) {
        if (valueMet) {
          setText(weightStatusEl, "You\u2019ve met the $250 goal but your cart is over 10 lbs. Current weight: " + weight.toFixed(1) + ' lbs.');
        } else {
          setText(weightStatusEl, 'Your cart weighs ' + weight.toFixed(1) + ' lbs. Free shipping requires orders under 10 lbs and over $250.');
        }
        show(weightStatusEl);
      }
      if (descriptionEl) hide(descriptionEl);
      return true;
    }

    if (tier === 'status') {
      banner.classList.add('progress-banner--status-mode');

      if (vpEl) hide(vpEl);
      if (wpEl) hide(wpEl);
      if (vtEl) hide(vtEl);
      if (wtEl) hide(wtEl);
      if (valueMetEl) hide(valueMetEl);
      if (weightStatusEl) hide(weightStatusEl);
      if (descriptionEl) hide(descriptionEl);

      if (statusTextEl) {
        setText(statusTextEl, 'Your cart weighs ' + weight.toFixed(1) + ' lbs. Free shipping is available on orders under 10 lbs and over $250.');
        show(statusTextEl);
      }
      return true;
    }

    // 'progress' tier, restore normal state
    if (descriptionEl) show(descriptionEl);
    if (vpEl) show(vpEl);
    if (wpEl) show(wpEl);
    if (vtEl) show(vtEl);
    if (wtEl) show(wtEl);
    return false;
  }

  // Read the server-side flags off #cart-metadata, which
  // snippets/of-cart-metadata.liquid renders on both cart surfaces.
  //
  // The attribute names here are the ones Liquid actually writes:
  // data-has-notfree and data-has-hazardous, which the dataset API spells
  // hasNotfree and hasHazardous. The old version also tried hasNotFree and
  // hasHazardousItem, spellings of data-has-not-free and
  // data-has-hazardous-item that no template has ever emitted, and tested
  // hasHazardous twice in one expression. Guessing at attribute names is
  // how a typo in the Liquid stays invisible.
  function readServerFlags() {
    try {
      const meta = document.getElementById('cart-metadata');
      if (!meta) return { notFree: false, hazardous: false };
      return {
        notFree: meta.dataset.hasNotfree === 'true',
        hazardous: meta.dataset.hasHazardous === 'true'
      };
    } catch (e) {
      return { notFree: false, hazardous: false };
    }
  }

  // DOM checks (fallback/live) for per-line dataset flags on cart <li>
  function domHasFlag(cartItems, flag) {
    if (!Array.isArray(cartItems)) return false;
    return cartItems.some(item => {
      const li = document.querySelector(`.cart-item[data-cartitem-key="${item.key}"]`);
      if (!li || !li.dataset) return false;
      if (flag === 'hazardous') return li.dataset.hazardous === 'true';
      if (flag === 'notFree') return li.dataset.notFree === 'true';
      return false;
    });
  }

  // main updater.
  //
  // @param suppliedCart optional cart JSON a caller already holds. The
  //   drawer's /cart/change.js response IS a cart; before it was passed
  //   through of:cart:updated, this function answered every mutation by
  //   fetching /cart.js to be told what the POST response had already
  //   said. Anything falsy here means "go and read the cart".
  function updateProgress(suppliedCart) {
    // Resolve the banner FIRST, every time. This is the re-entry point the
    // drawer needs: a section swap replaces the node, and a page that had
    // no banner at DOMContentLoaded can grow one later. A missing banner is
    // NOT a reason to stop: the $35 gate is rendered for every shopper, the
    // banner only for contiguous-US ones, so returning here left non-US
    // shoppers on /cart with a gate frozen at its page-load state through
    // every quantity edit.
    banner = resolveBanner();
    readConfig();

    // Use shared dedup helper when available; fall back to own fetch.
    // getCartShared() handles the inline __OF_CART_CACHE one-shot and the
    // 1-second TTL cache, so multiple scripts in the same burst share one fetch.
    const fetchCart = (suppliedCart && suppliedCart.items)
      ? Promise.resolve(suppliedCart)
      : (window.__OFNudgesCore && window.__OFNudgesCore.getCartShared)
        ? window.__OFNudgesCore.getCartShared()
        : fetch(CART_JSON, { credentials: 'same-origin', cache: 'no-store' })
            .then(r => (r.ok ? r.json() : cartReadFailed()))
            .catch(cartReadFailed);

    fetchCart.then(cart => {
      // A failed read is not an empty cart. Both sources above resolve to
      // the same flagged sentinel rather than rejecting, and this is the
      // only place that has to know the difference: everything below reads
      // total_price and items as truth, so treating a failure as {items:
      // [], total_price: 0} printed zeros, lifted the $35 gate and wiped a
      // hazardous state that was still true. Hold the last good render
      // instead. A banner that has never rendered has nothing to hold, so
      // it takes its space back rather than sitting at "Loading...".
      if (!cart || cart.__ofFetchFailed) {
        if (banner && !banner.classList.contains('js-visible')) hideBannerSpace();
        return;
      }

      // The $35 minimum gate on /cart is Liquid-rendered and Empire's AJAX
      // morph never touches it, so a quantity edit crossing the line left
      // the checkout buttons stuck in their page-load state (code review
      // 8/31). It runs BEFORE the banner guard below and independently of
      // it: the gate is a property of the cart, not of the banner, and
      // it exists on the page whether or not free shipping copy does. The
      // drawer's own gate re-renders with its section swap and needs
      // nothing here.
      updateMinimumGate(cart.total_price);

      if (!banner) return;

      // Reset the state classes only once the new data is in hand. This
      // used to run synchronously, before the fetch, and it took js-visible
      // with it: of-cart.css holds .progress-banner at visibility:hidden
      // until that class is present, so every quantity edit on /cart blanked
      // the banner for a full round trip and then painted it back. The
      // previous render now stays on screen until this one can replace it.
      // js-visible is deliberately NOT in this list; showBanner() adds it
      // and hidden-by-outlier (display:none) is what actually hides.
      banner.classList.remove('hidden-by-outlier', 'progress-banner--hazardous', 'progress-banner--oversized', 'progress-banner--split-status', 'progress-banner--status-mode', 'progress-banner--unlocked');
      banner.style.visibility = '';

      // 1) special flags (server-side quick check + DOM fallback)
      //
      // Three-case source-of-truth rule:
      //   1. Empty cart (cart.items.length === 0): both flags are false,
      //      full stop. An empty cart has no flagged items by definition,
      //      so neither the DOM nor a possibly-stale server flag should be
      //      consulted at all.
      //   2. Cart has items AND at least one of those items' keys resolves
      //      to a rendered .cart-item[data-cartitem-key] element: DOM is
      //      AUTHORITATIVE, scoped ONLY to the matched lines (not the full
      //      cart, and not the server flag). #cart-metadata (the server
      //      source) only refreshes on a full section reload, so a
      //      partial-morph update (e.g. removing the last flagged line)
      //      can leave it stale at "true" while the live DOM has already
      //      dropped every hazardous/not-free line; trusting server here
      //      would wedge the banner forever with no way for the DOM to
      //      correct it.
      //   3. Cart has items but NONE of their keys resolve in the DOM
      //      (stale or not-yet-rendered DOM, e.g. a render/fetch race):
      //      nothing live to check, so fall back to the server flags.
      const server = readServerFlags();
      let hasHazardous, hasNotFree;
      if (!cart.items || cart.items.length === 0) {
        hasHazardous = false;
        hasNotFree = false;
      } else {
        const matchedLines = cart.items.filter(item =>
          document.querySelector(`.cart-item[data-cartitem-key="${item.key}"]`)
        );
        if (matchedLines.length > 0) {
          hasHazardous = domHasFlag(matchedLines, 'hazardous');
          hasNotFree = domHasFlag(matchedLines, 'notFree');
        } else {
          hasHazardous = server.hazardous;
          hasNotFree = server.notFree;
        }
      }

      if (hasHazardous) {
        showBanner();
        setSpecialState({ hazardous: true });
        publishFreeshipState({ visible: true, special: 'hazardous', remainCents: 0, eligibleGrams: 0, maxWeightLbs: MAX_WEIGHT, minValue: MIN_VALUE });
        return;
      }
      if (hasNotFree) {
        showBanner();
        setSpecialState({ oversized: true });
        publishFreeshipState({ visible: true, special: 'oversized', remainCents: 0, eligibleGrams: 0, maxWeightLbs: MAX_WEIGHT, minValue: MIN_VALUE });
        return;
      }

      // Idempotent full-state reset: this render is NOT hazardous/oversized.
      // Without this call, transitioning from hazardous/oversized back to
      // normal never revisits setSpecialState() at all, since the rest of
      // this function only calls renderTier(), which manages the
      // value/weight bars but never touches #hazard-text/#oversize-text or
      // the progress-banner--hazardous/--oversized classes. That left the
      // stale red hazard/oversize message visible underneath the recovered
      // headline + bars for as long as the AJAX-repainted DOM persisted
      // (until a full section morph replaced the markup). Calling this
      // unconditionally on every non-special render makes the render
      // correct regardless of what state preceded it, not a delta from the
      // previous render.
      setSpecialState();

      // 2) outlier: hide banner space entirely if any single NON-special item unit disqualifies
      //
      // "NON-special" is load bearing and is the second line of the test,
      // not an afterthought. A line whose product carries the
      // 'Free Shipping Specials' tag ships free on its own terms, so no
      // weight and no price can disqualify it, and it must never make the
      // cart an outlier no matter how heavy or expensive it is. The
      // Durston benches are the case that proves it: 113.045 is $895.00
      // and 115 lbs, both thresholds blown, and the banner still has an
      // honest and cheerful thing to say about that cart.
      //
      // The tag is read off the rendered line's dataset, written by
      // snippets/cart-item.liquid as data-free-shipping-specials from the
      // same 'Free Shipping Specials' tag check of-cart-item-messages.liquid
      // uses. The DOM is the only source available here: /cart.js item
      // objects carry no product tags. Step 4 below excludes the same
      // lines from the weight total, off the same dataset flag, so the
      // two halves of the rule agree.
      const outlier = cart.items.some(item => {
        const li = document.querySelector(`.cart-item[data-cartitem-key="${item.key}"]`);
        if (!li) return false;
        if (li.dataset.freeShippingSpecials === 'true') return false;
        const unitLbs = item.grams / 453.592;
        const unitPrice = item.price / 100;
        return unitLbs > MAX_WEIGHT && unitPrice > MIN_VALUE;
      });
      if (outlier) {
        hideBannerSpace();
        publishFreeshipState({ visible: false, special: 'outlier', remainCents: 0, eligibleGrams: 0, maxWeightLbs: MAX_WEIGHT, minValue: MIN_VALUE });
        return;
      }

      // GA4: view_cart (once per page load).
      //
      // Gated on isVisible(). The drawer renders this banner on every page
      // type, so without the gate every single pageview with a non-empty
      // cart would fire view_cart and the 18,765-per-30-days baseline would
      // become meaningless overnight. On /cart there is no drawer and the
      // gate is always true, which is the old behaviour exactly.
      if (window.OFTrack && !window.__ofViewCartFired && isVisible(banner)) {
        window.__ofViewCartFired = true;
        var gaItems = (cart.items || []).map(function(it) {
          return { item_id: String(it.variant_id), item_name: it.title, price: it.price / 100, quantity: it.quantity };
        });
        OFTrack.event('view_cart', { currency: 'USD', value: cart.total_price / 100, items: gaItems, surface: surfaceOf(banner) });
      }

      // 3) Order value (all items count)
      const totalD = cart.total_price / 100;
      const remain = Math.max(MIN_VALUE - totalD, 0);
      const pctVal = Math.min((totalD / MIN_VALUE) * 100, 100);
      const vpEl = $('#value-progress');
      const vtEl = $('#value-text');
      const vf = vpEl ? vpEl.querySelector('.progress-bar__fill') : null;
      if (vpEl && vtEl && vf) {
        vpEl.classList.toggle('is-pending', remain > 0);
        vpEl.classList.toggle('is-unlocked', remain <= 0);
        vf.style.width = pctVal + '%';
        vtEl.textContent = remain > 0
          ? `Add ${formatMoney(remain * 100)} more to unlock free shipping`
          : '$250 Order Value Minimum Met';
      }

      // 4) Weight (exclude Free Shipping Specials)
      const eligible = cart.items.filter(item => {
        const li = document.querySelector(`.cart-item[data-cartitem-key="${item.key}"]`);
        return !(li && li.dataset.freeShippingSpecials === 'true');
      });
      const totalG = eligible.reduce((s, i) => s + (i.grams * i.quantity), 0);
      // weightExact drives every eligibility comparison/state decision below.
      // weight is rounded to one decimal for DISPLAY ONLY -- comparing the
      // rounded value against MAX_WEIGHT let 10.0-10.049 lb carts show as
      // eligible while checkout (which uses the exact weight) disqualified them.
      const weightExact = totalG / 453.592;
      const weight = Math.round(weightExact * 10) / 10;
      const pctW = Math.min((weight / MAX_WEIGHT) * 100, 100);
      const wpEl = $('#weight-progress');
      const wf = wpEl ? wpEl.querySelector('.progress-bar__fill') : null;
      const wtEl = $('#weight-text');

      // The weight bar only ever paints UNDER the cap. Every over-cap
      // weight lands in split-status, status or hidden a few lines below,
      // and each of those hides this bar and its label in the same render.
      // The red is-overweight state and the "You have exceeded 10 lbs. Over
      // by X lbs" string could therefore never appear on screen; they were
      // written, styled in both stylesheets, and dead (review 9/1). Gone,
      // along with the overage rounding they needed. The tiers own the
      // over-10 message. The label is left untouched rather than written
      // with an under-cap sentence that would be false if it ever showed.
      if (wpEl && wf && wtEl) {
        wf.style.width = pctW + '%';
        wpEl.classList.toggle('is-ok', weightExact <= MAX_WEIGHT);
        if (weightExact <= MAX_WEIGHT) {
          wtEl.textContent = `You are under 10 lbs. Current Weight: ${weight} lbs`;
          if (eligible.length !== cart.items.length) wtEl.textContent += ' (excludes free-shipping items)';
        }
      }

      // GA4: free shipping state transitions.
      //
      // Gated on isVisible() for the same reason view_cart is, above: the
      // drawer renders this banner on every page, and a cart edited from a
      // PDP would otherwise log unlocks and losses against a banner nobody
      // ever saw. The state machine is inside the gate on purpose. A
      // transition is only SPENT when it was actually shown, so the first
      // paint the customer does see compares against the last state they
      // were shown rather than against one that happened off screen.
      if (window.OFTrack && isVisible(banner)) {
        var valueOk = remain <= 0;
        var weightOk = weightExact <= MAX_WEIGHT;

        // Detect transitions (skip first run where state is null)
        if (_freeshipState.valueOk !== null) {
          // Unlocked: both conditions now met, at least one wasn't before
          if (valueOk && weightOk && !(_freeshipState.valueOk && _freeshipState.weightOk)) {
            OFTrack.event('of_freeship_unlocked', {
              cart_value: totalD, cart_weight: weight, item_count: cart.items.length,
              surface: surfaceOf(banner)
            });
          }

          // Lost due to value drop
          if (!valueOk && _freeshipState.valueOk) {
            OFTrack.event('of_freeship_lost_value', {
              cart_value: totalD, deficit: Math.round(remain * 100) / 100,
              surface: surfaceOf(banner)
            });
          }

          // Lost due to weight. GA4 gets the exact overage (2-decimal
          // precision from weightExact), not the display's round-up-to-0.1
          // value -- analytics wants the true number, not the
          // never-shows-zero UX rounding.
          if (!weightOk && _freeshipState.weightOk) {
            OFTrack.event('of_freeship_lost_weight', {
              cart_weight: weight, cart_value: totalD,
              overage: Math.round((weightExact - MAX_WEIGHT) * 100) / 100,
              surface: surfaceOf(banner)
            });
          }

          // Weight trim: weight was over, now under (item removed or quantity decreased)
          if (weightOk && !_freeshipState.weightOk) {
            // Find which item was removed or had quantity decreased
            var curKeys = new Set(cart.items.map(function(it) { return it.key; }));
            var removedTitle = 'unknown';
            for (var ri = 0; ri < _freeshipState.prevItems.length; ri++) {
              if (!curKeys.has(_freeshipState.prevItems[ri].key)) {
                removedTitle = _freeshipState.prevItems[ri].title || 'unknown';
                break;
              }
            }
            if (removedTitle === 'unknown') removedTitle = 'quantity adjusted';
            OFTrack.event('of_freeship_weight_trim', {
              cart_value: totalD,
              cart_weight_before: _freeshipState.prevWeight,
              cart_weight_after: weight,
              item_removed_title: removedTitle,
              surface: surfaceOf(banner)
            });
          }
        }

        _freeshipState.valueOk = valueOk;
        _freeshipState.weightOk = weightOk;
        _freeshipState.prevWeight = weight;
        _freeshipState.prevItems = (cart.items || []).map(function(it) { return { key: it.key, title: it.title }; });
      }

      // 5) Tier detection & header
      const SPLIT_STATUS_MAX = 15;
      const BANNER_HIDE_WEIGHT = 20;
      const tier = weightExact <= MAX_WEIGHT ? 'progress'
                 : weightExact <= SPLIT_STATUS_MAX ? 'split-status'
                 : weightExact <= BANNER_HIDE_WEIGHT ? 'status'
                 : 'hidden';

      if (tier === 'hidden') {
        hideBannerSpace();
        publishFreeshipState({ visible: false, special: 'weight', remainCents: Math.round(remain * 100), eligibleGrams: totalG, maxWeightLbs: MAX_WEIGHT, minValue: MIN_VALUE });
        return;
      }

      const headerEl = $('#progress-header');
      const meetsAll = (remain <= 0 && weightExact <= MAX_WEIGHT);

      if (tier === 'progress') {
        renderTier('progress', totalD, remain, weight);
        if (headerEl) headerEl.textContent = meetsAll ? "🎉 You've unlocked free shipping!" : '🚚 Unlock Free Shipping';
        // renderTier() cleared this a line ago, so setting it here is the
        // whole state machine for the class. The drawer reads it to show
        // the win and only the win.
        banner.classList.toggle('progress-banner--unlocked', meetsAll);
      } else if (tier === 'split-status') {
        renderTier('split-status', totalD, remain, weight);
        if (headerEl) headerEl.textContent = '📦 Standard Shipping Applies';
      } else {
        renderTier('status', totalD, remain, weight);
        if (headerEl) headerEl.textContent = '📦 Standard Shipping Applies';
      }

      // Unlock pulse removed 2026-05-05: read as easy-to-miss and odd on a
      // quantity change. The banner state speaks for itself through colour
      // and copy. The sessionStorage gating helpers and triggerUnlockPulse
      // were kept "in case we want a different unlock affordance later" and
      // sat unreferenced for four months, next to an is-unlock-celebrating
      // class no stylesheet defines. Deleted 9/2; git has them.

      // show normal banner (not outlier or special)
      showBanner();
      // tier is 'progress', 'split-status' or 'status' here ('hidden' already
      // returned above). Only 'progress' is a cart the top-off row may act
      // on: split-status and status are already over the weight cap, and a
      // dollar-only suggestion there would win the money half of free
      // shipping while losing the weight half, which is worse than no
      // suggestion at all.
      publishFreeshipState({
        visible: true,
        special: tier === 'progress' ? null : 'weight',
        remainCents: Math.round(remain * 100),
        eligibleGrams: totalG,
        maxWeightLbs: MAX_WEIGHT,
        minValue: MIN_VALUE
      });
    }).catch(err => {
      // Reached only when the render itself throws; a failed cart read is
      // handled above and never gets here. Showing the banner on the way
      // out would reveal a half-written or "Loading..." state, so this
      // leaves whatever was on screen exactly where it was.
      console.warn('Free shipping banner: render failed', err);
    });
  }

  // helpers that manipulate the banner's visibility
  //
  // A surface may wrap the banner in a section that carries a heading of
  // its own (the cart drawer's "Free shipping check" block). That heading
  // has to live and die with the banner, or a cart that trips one of the
  // hide rules (the outlier line, or a cart over BANNER_HIDE_WEIGHT) is
  // left with a section title standing over blank space. Any wrapper that
  // wants that behaviour marks itself [data-fsb-section]; there is no
  // second copy of the rule list here or in CSS, the wrapper simply
  // follows whatever showBanner/hideBannerSpace already decided.
  // Surfaces with no such wrapper (the /cart page) resolve to null and are
  // untouched.
  function bannerSection() {
    if (!banner || !banner.closest) return null;
    return banner.closest('[data-fsb-section]');
  }
  function showBanner() {
    if (!banner) return;
    banner.classList.remove('hidden-by-outlier');
    banner.classList.add('js-visible');
    banner.style.visibility = '';
    var sec = bannerSection();
    if (sec) sec.removeAttribute('hidden');
  }
  function hideBannerSpace() {
    if (!banner) return;
    banner.classList.add('hidden-by-outlier');
    var sec = bannerSection();
    if (sec) sec.setAttribute('hidden', '');
  }

  // The subtotal observer is a cart-change signal, and the node it watches
  // is replaced by every section swap, so the binding has to be redoable.
  var _subObserver = null;
  function bindSubtotal() {
    if (_subObserver) { try { _subObserver.disconnect(); } catch (e) {} _subObserver = null; }
    if (!window.MutationObserver) return;
    const subEl = document.querySelector('[data-html="cart_total"]');
    if (!subEl) return;
    // Coalesced, like every other repaint trigger. Empire's morphdom
    // rewrites the subtotal on each cart edit, and a run of stepper taps
    // fires this observer once per edit; without the shared timer each one
    // ran its own full render.
    _subObserver = new MutationObserver(() => scheduleUpdate(60));
    _subObserver.observe(subEl, { childList: true, characterData: true, subtree: true });
  }

  // The documented re-entry point. of-cart-drawer.js calls refresh() after
  // it swaps the drawer's content, and on open.
  window.__OFProgressBanner = {
    refresh: updateProgress,
    rebind: function () { bindSubtotal(); updateProgress(); }
  };

  // ONE pending repaint, never one per click.
  //
  // Every stepper press used to start its own setTimeout, so holding the
  // plus button through eight taps queued eight timers and ran eight full
  // renders, each with its own cart read behind it. The cart drawer's own
  // quantity writes have always been debounced this way (scheduleQty in
  // of-cart-drawer.js); this is the same shape, one handle, cleared and
  // replaced. The most recent call wins outright, including its cart
  // argument: a click arriving after a cart-carrying event supersedes it
  // with a fresh read, which is the safe direction to resolve that race.
  var _updateTimer = null;
  var _pendingCart = null;
  function scheduleUpdate(delay, cart) {
    if (_updateTimer) clearTimeout(_updateTimer);
    _pendingCart = cart || null;
    _updateTimer = setTimeout(function () {
      _updateTimer = null;
      var c = _pendingCart;
      _pendingCart = null;
      updateProgress(c);
    }, delay);
  }

  // wire events. Delegated on document.body, so the listeners survive any
  // number of section swaps without rebinding.
  function boot() {
    updateProgress();
    bindSubtotal();
    document.body.addEventListener('click', function (e) {
      if (e.target.matches && e.target.closest && e.target.closest('[data-quantity-plus], [data-quantity-minus]')) scheduleUpdate(300);
    });
    document.body.addEventListener('change', function (e) {
      if (e.target.matches && e.target.matches('[data-quantity-input]')) scheduleUpdate(300);
    });
  }
  // DOMContentLoaded, not readyState !== 'loading'. This is a deferred
  // script, so at execution time readyState is 'interactive' and booting
  // right here would run before the later deferred tags (of-nudges-core.js
  // among them) have executed, costing the shared cart cache and the
  // inline __OF_CART_CACHE one-shot on first paint.
  if (document.readyState === 'complete') boot();
  else document.addEventListener('DOMContentLoaded', boot, { once: true });

  // Optimistic paint event: refresh the banner on cart mutation. The drawer
  // publishes this with the new root in detail after a swap, and with the
  // cart itself when the mutation produced one.
  //
  // With a cart in hand there is nothing to settle and nothing to fetch, so
  // the render runs on the next tick. Without one the 50ms delay stays: it
  // is there to let the shared cart cache's invalidation land before we ask
  // it for a cart.
  document.addEventListener('of:cart:updated', (ev) => {
    var supplied = (ev && ev.detail && ev.detail.cart) ? ev.detail.cart : null;
    bindSubtotal();
    scheduleUpdate(supplied ? 0 : 50, supplied);
  });

})();
